'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { Tooltip } from 'src/components/PulseUI';
import { getProfileUrl } from 'src/utils/profileUrl';

interface ListItem {
  name: string;
  provider_id?: string;
  type?: string;
  [key: string]: any;
}

interface ListItemsCellProps {
  row: any;
  field: string;
}

const ListItemsCell: React.FC<ListItemsCellProps> = ({ row, field }) => {
  const items = useMemo(() => {
    const rawItems =
      row[field] || row.hcp?.[field] || row.provider?.[field] || [];
    return [...rawItems].sort((a, b) => a.name.localeCompare(b.name));
  }, [row, field]);

  if (!items.length) return null;

  const showAllItems = items.length === 1;
  const visibleItems = items.slice(0, 1);
  const hiddenCount = showAllItems ? 0 : items.length - 1;

  // Check if items have profile links (affiliations with provider_id and type)
  const hasProfileLinks =
    field === 'affiliations' && items[0]?.provider_id && items[0]?.type;

  const getItemProfileUrl = (item: ListItem) => {
    if (!item.provider_id || !item.type) return null;
    return getProfileUrl(item.type, item.provider_id);
  };

  const renderItem = (item: ListItem, index: number) => {
    if (hasProfileLinks) {
      const profileUrl = getItemProfileUrl(item);
      if (profileUrl) {
        return (
          <Link
            key={`${item.name}-${index}`}
            href={profileUrl}
            onClick={(e) => e.stopPropagation()}
            className="no-underline text-primary hover:underline text-sm"
          >
            {item.name}
          </Link>
        );
      }
    }

    return (
      <span key={`${item.name}-${index}`} className="text-sm">
        {item.name}
      </span>
    );
  };

  const tooltipContent = (
    <div className="flex flex-col gap-1">
      {items.map((item, index) => {
        const textContent = (
          <span
            className={`text-xs text-white ${index >= 2 ? 'font-extrabold' : 'font-normal'}`}
          >
            {item.name}
          </span>
        );

        if (hasProfileLinks) {
          const profileUrl = getItemProfileUrl(item);
          if (profileUrl) {
            return (
              <Link
                key={index}
                href={profileUrl}
                onClick={(e) => e.stopPropagation()}
                className="no-underline text-white hover:underline"
              >
                {textContent}
              </Link>
            );
          }
        }

        return <div key={index}>{textContent}</div>;
      })}
    </div>
  );

  return (
    <div className="overflow-hidden">
      <div className="overflow-hidden whitespace-nowrap text-ellipsis">
        {visibleItems.map((item: ListItem, index) => (
          <span key={`wrapper-${index}`}>
            {renderItem(item, index)}
            {index < visibleItems.length - 1 && ', '}
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <Tooltip content={tooltipContent} delay={500}>
          <span className="underline cursor-pointer text-sm">
            +{hiddenCount} more
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default ListItemsCell;
